import React from 'react';
import 'twin.macro';
import { Navigate, useNavigate } from 'react-router-dom';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { useBillingInfos } from 'queries/useBillingInfos';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { TeetshBackground } from '@teetsh/app/src/components/TeetshBackground';
import { SingleCardPage } from 'components/Layouts/SingleCardPage';
import { format } from '@teetsh/app/src/utils/time/format';
import { useGetPlans } from 'queries/plan/useGetPlans';
import { convertPlanDays } from './convertPlanDays';
import { ConversionOptionDescription } from './ConversionOptionDescription';
import ListWithDescription from 'components/RadioGroups/ListWithDescription';
import Button from 'components/Buttons/Button';
import { UserLocale } from '@teetsh/app/src/types/user';
import subDays from 'utils/time/subDays';
import { GRACE_PERIODE_IN_DAYS } from 'containers/constants';

function extractPlanFromLicenseCode(licenseCode: string): string | undefined {
  if (licenseCode.startsWith('LIE')) {
    return 'essentiel';
  }
  if (licenseCode.startsWith('LII')) {
    return 'integral';
  }
  return undefined;
}

export type ConversionOption = 'keepPlan' | 'switchPlan';

export function RedeemPage() {
  const licenseCode = new URLSearchParams(location.search).get('code');
  const { data: allPlans = [] } = useGetPlans();
  const { data: billingInfos, isLoading: isLoadingBillingInfos } =
    useBillingInfos();
  const [conversionOption, setConversionOption] =
    React.useState<ConversionOption>('switchPlan');
  const { t } = useTranslation(['userSettings', 'common']);
  const { notifyError } = useNotification();
  const navigate = useNavigate();

  const licensePlanKey = extractPlanFromLicenseCode(licenseCode || '');
  if (isLoadingBillingInfos) {
    return <SpinnerComponent />;
  }
  const currentPlan = allPlans.find(p => p.id === billingInfos?.plan);
  const licensePlan = allPlans.find(p => p.id === licensePlanKey);

  if (!licensePlanKey || !billingInfos) {
    notifyError({
      title: t('userSettings:pricing.coupon.invalidLicense'),
    });
    return <Navigate to="/dashboard" />;
  }

  if (
    !billingInfos.isOnFreeOrTrialPlan &&
    !!billingInfos.expirationDate &&
    billingInfos.remainingDays &&
    !!currentPlan &&
    !!licensePlan
  ) {
    // the user already has a paid plan so we explain what happens and let him choose between options (if any)
    const isSamePlan = currentPlan.id === licensePlan.id;
    const lostFeatures = currentPlan.features.filter(
      f => !licensePlan.features.includes(f),
    );
    const newFeatures = licensePlan.features.filter(
      f => !currentPlan.features.includes(f),
    );
    const renewalDate = subDays(
      billingInfos.expirationDate,
      GRACE_PERIODE_IN_DAYS,
    );
    return (
      <TeetshBackground>
        <TeetshBackground.Header />
        <TeetshBackground.Content>
          <SingleCardPage tw="w-full" maxWidth="lg">
            <div tw="mx-auto max-w-7xl py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
              <h2 tw="text-3xl font-bold text-center tracking-tight text-neutral-intense sm:text-4xl text-balance">
                {t('userSettings:pricing.licenseActivation.title', {
                  label: licensePlan.label,
                })}
              </h2>
              <div tw="text-lg max-w-lg mx-auto mt-4">
                {t(
                  'userSettings:pricing.licenseActivation.youHaveAnExistingPlan',
                  {
                    currentPlanLabel: currentPlan.label,
                    currentPlanExpirationDate: format(
                      renewalDate,
                      t('common:locale.long') as UserLocale,
                      t('common:datePattern.dd/MM/yyyy'),
                    ),
                    remainingDays: billingInfos.remainingDays,
                  },
                )}{' '}
                {isSamePlan
                  ? t(
                      'userSettings:pricing.licenseActivation.activateLicenseAndExtend',
                    )
                  : t(
                      'userSettings:pricing.licenseActivation.activateLicenseAndSwitch',
                      {
                        label: licensePlan.label,
                      },
                    )}
              </div>
              {!isSamePlan && (
                <div tw="mt-8">
                  <ListWithDescription<ConversionOption>
                    radioPosition="top"
                    options={[
                      {
                        value: 'switchPlan',
                        title: t(
                          'userSettings:pricing.licenseActivation.switchPlan',
                          {
                            label: licensePlan.label,
                          },
                        ),
                        description: (
                          <ConversionOptionDescription
                            conversionOption="switchPlan"
                            additionalDays={convertPlanDays(
                              billingInfos.remainingDays,
                              currentPlan,
                              licensePlan,
                            )}
                            lostFeatures={lostFeatures}
                            newFeatures={newFeatures}
                          />
                        ),
                      },
                      {
                        value: 'keepPlan',
                        title: t(
                          'userSettings:pricing.licenseActivation.extendPlan',
                          {
                            label: currentPlan.label,
                          },
                        ),
                        description: (
                          <ConversionOptionDescription
                            conversionOption="keepPlan"
                            additionalDays={convertPlanDays(
                              365,
                              licensePlan,
                              currentPlan,
                            )}
                            lostFeatures={[]}
                            newFeatures={[]}
                          />
                        ),
                      },
                    ]}
                    value={conversionOption}
                    onChange={v => setConversionOption(v)}
                  />
                </div>
              )}
              <div tw="mt-8 flex justify-center">
                <Button
                  tw="ml-3 text-balance text-center"
                  extralarge
                  intent="primary"
                  onClick={() => {
                    const desiredPlan =
                      conversionOption === 'keepPlan'
                        ? currentPlan.id
                        : licensePlan.id;
                    return navigate(
                      `/subscription/checkout?coupon=${licenseCode}&recurrence=YEARLY&plan=${desiredPlan}&userOption=${conversionOption}`,
                    );
                  }}
                >
                  {isSamePlan || conversionOption === 'keepPlan'
                    ? t('userSettings:pricing.licenseActivation.extendPlan', {
                        label: currentPlan.label,
                      })
                    : t('userSettings:pricing.licenseActivation.switchPlan', {
                        label: licensePlan.label,
                      })}
                </Button>
              </div>
            </div>
          </SingleCardPage>
        </TeetshBackground.Content>
      </TeetshBackground>
    );
  }

  return (
    <Navigate
      to={`/subscription/checkout?coupon=${licenseCode}&recurrence=YEARLY&plan=${licensePlanKey}`}
    />
  );
}
